import axios from "axios";

export const API_BASE = "/api";

const TOKEN_KEY = "ll_session_token";

export function getToken() {
    return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
});

export const api = {
    login: (email, password) =>
        client.post("/auth/login", { email, password }).then((r) => r.data),
    register: (payload) =>
        client.post("/auth/register", payload).then((r) => r.data),
    googleExchange: (session_id) =>
        client.post("/auth/google/exchange", { session_id }).then((r) => r.data),
    me: () => client.get("/auth/me").then((r) => r.data),
    logout: () => client.post("/auth/logout").then((r) => r.data),

    businesses: () => client.get("/businesses").then((r) => r.data),
    business: (id) => client.get(`/businesses/${id}`).then((r) => r.data),
    kpis: (id) => client.get(`/businesses/${id}/kpis`).then((r) => r.data),
    briefing: (id) => client.get(`/businesses/${id}/briefing`).then((r) => r.data),
    regenBriefing: (id) =>
        client.post(`/businesses/${id}/briefing/regenerate`).then((r) => r.data),
    profile: (id) => client.get(`/businesses/${id}/profile`).then((r) => r.data),

    reviews: (id, params = {}) =>
        client.get(`/businesses/${id}/reviews`, { params }).then((r) => r.data),
    genReply: (rid) =>
        client.post(`/reviews/${rid}/generate_reply`).then((r) => r.data),

    complaints: (id, params = {}) =>
        client.get(`/businesses/${id}/complaints`, { params }).then((r) => r.data),
    patchComplaint: (cid, patch) =>
        client.patch(`/complaints/${cid}`, patch).then((r) => r.data),

    approvals: (id, status = "pending") =>
        client.get(`/businesses/${id}/approvals`, { params: { status } }).then((r) => r.data),
    decideApproval: (aid, action, edited_payload) =>
        client.post(`/approvals/${aid}/decide`, { action, edited_payload }).then((r) => r.data),

    memory: (id) => client.get(`/businesses/${id}/memory`).then((r) => r.data),
    updateMemory: (id, patch) =>
        client.put(`/businesses/${id}/memory`, patch).then((r) => r.data),
    autonomy: (id) => client.get(`/businesses/${id}/autonomy`).then((r) => r.data),
    updateAutonomy: (id, patch) =>
        client.put(`/businesses/${id}/autonomy`, patch).then((r) => r.data),

    weekly: (id) => client.get(`/businesses/${id}/reports/weekly`).then((r) => r.data),
    genWeekly: (id) =>
        client.post(`/businesses/${id}/reports/generate`).then((r) => r.data),
    askData: (id, question) =>
        client.post(`/businesses/${id}/reports/ask`, { question }).then((r) => r.data),

    chat: (business_id, text, session_id) =>
        client.post("/ai/chat", { business_id, text, session_id }).then((r) => r.data),
    chatHistory: (business_id, session_id) =>
        client.get("/ai/history", { params: { business_id, session_id } }).then((r) => r.data),

    integrationsLegacy: () => client.get("/integrations/status").then((r) => r.data),

    features: () => client.get("/features").then((r) => r.data),

    // Phase 4 — Monitoring & Alerts
    runMonitoring: (id) =>
        client.post(`/businesses/${id}/monitoring/run`).then((r) => r.data),
    alerts: (id, params = {}) =>
        client.get(`/businesses/${id}/alerts`, { params }).then((r) => r.data),
    markAlertRead: (aid) =>
        client.post(`/alerts/${aid}/read`).then((r) => r.data),

    // Phase 4 — Reporting Upgrade
    periodReport: (id, period) =>
        client.get(`/businesses/${id}/reports/period`, { params: { period } }).then((r) => r.data),
    genPeriodReport: (id, period) =>
        client.post(`/businesses/${id}/reports/period/generate`, { period }).then((r) => r.data),
    listPeriodReports: (id, period) =>
        client.get(`/businesses/${id}/reports/list`, { params: period ? { period } : {} }).then((r) => r.data),
    askReport: (rid, question) =>
        client.post(`/reports/${rid}/ask`, { question }).then((r) => r.data),
    reportPdfUrl: (rid) => `${API_BASE}/reports/${rid}/pdf`,
    reportCsvUrl: (rid) => `${API_BASE}/reports/${rid}/csv`,
    downloadReport: async (rid, kind) => {
        // Axios client already has baseURL='/api' — use a relative path
        // so we don't end up with /api/api/... Also set responseType=blob
        // so the auth interceptor still runs and cookie/Bearer is sent.
        const r = await client.get(`/reports/${rid}/${kind}`, {
            responseType: "blob",
        });
        const blob = new Blob([r.data], {
            type: kind === "pdf" ? "application/pdf" : "text/csv",
        });
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        const cd = r.headers["content-disposition"] || "";
        const match = /filename="?([^"]+)"?/.exec(cd);
        link.download = match ? match[1] : `report.${kind}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(objectUrl), 500);
    },
    reportSchedules: (id) =>
        client.get(`/businesses/${id}/report-schedules`).then((r) => r.data),
    saveReportSchedule: (id, payload) =>
        client.put(`/businesses/${id}/report-schedules`, payload).then((r) => r.data),

    // Phase 4 — Super Admin
    adminOverview: () => client.get("/admin/overview").then((r) => r.data),
    adminTenants: () => client.get("/admin/tenants").then((r) => r.data),
    adminUsers: () => client.get("/admin/users").then((r) => r.data),
    adminAuditLog: (params = {}) =>
        client.get("/admin/audit-log", { params }).then((r) => r.data),
    adminAiUsage: (params = {}) =>
        client.get("/admin/ai-usage", { params }).then((r) => r.data),
    adminSetFlag: (tenantId, module, enabled) =>
        client.put(`/admin/tenants/${tenantId}/flags`, { module, enabled }).then((r) => r.data),
    adminModules: () => client.get("/admin/modules").then((r) => r.data),

    // Phase 5 — Real Discovery + Company Profile
    discoverySearch: (name, city, description) =>
        client.post("/discovery/search", { name, city, description }).then((r) => r.data),
    discoveryProviders: () =>
        client.get("/discovery/providers").then((r) => r.data),
    discoveryRefine: (payload) =>
        client.post("/discovery/refine", payload).then((r) => r.data),
    discoveryVerifyUrl: (url, field, name) =>
        client.post("/discovery/verify-url", { url, field, name }).then((r) => r.data),
    discoveryConfirm: (payload) =>
        client.post("/discovery/confirm", payload).then((r) => r.data),
    companyProfile: (id) =>
        client.get(`/businesses/${id}/profile`).then((r) => r.data),
    updateProfileField: (id, field, value) =>
        client.put(`/businesses/${id}/profile/field`, { field, value }).then((r) => r.data),
    verifyProfileField: (id, platform) =>
        client.post(`/businesses/${id}/profile/verify`, { platform }).then((r) => r.data),
    profileRediscover: (id) =>
        client.post(`/businesses/${id}/profile/rediscover`).then((r) => r.data),
    acceptRediscoverChange: (id, field, value) =>
        client.post(`/businesses/${id}/profile/rediscover/accept`, { field, value }).then((r) => r.data),
    profileCompleteness: (id) =>
        client.get(`/businesses/${id}/profile/completeness`).then((r) => r.data),
    profileFixWithAI: (id, platform, item, fix_kind, context = {}) =>
        client.post(`/businesses/${id}/profile/fix-with-ai`, { platform, item, fix_kind, context }).then((r) => r.data),

    onboardingDiscover: (name, city, description) =>
        client.post("/onboarding/discover", { name, city, description }).then((r) => r.data),

    // Presence analysis (per-handle + AI)
    presenceAnalyse: (id, run_audit = false) =>
        client.post(`/businesses/${id}/presence/analyse`, { run_audit }).then((r) => r.data),
    presenceLatest: (id) =>
        client.get(`/businesses/${id}/presence/latest`).then((r) => r.data),
    presenceGet: (id, analysisId) =>
        client.get(`/businesses/${id}/presence/analyses/${analysisId}`).then((r) => r.data),
    onboardingConfirm: (payload) =>
        client.post("/onboarding/confirm", payload).then((r) => r.data),

    auditLatest: (id) => client.get(`/businesses/${id}/audit/latest`).then((r) => r.data),
    auditRun: (id) => client.post(`/businesses/${id}/audit/run`).then((r) => r.data),
    auditGet: (auditId) => client.get(`/audits/${auditId}`).then((r) => r.data),
    auditFix: (auditId, issue_id) =>
        client.post(`/audits/${auditId}/fix`, { issue_id }).then((r) => r.data),
    auditFixAll: (auditId, only_safe = true) =>
        client.post(`/audits/${auditId}/fix_all`, { audit_id: auditId, only_safe }).then((r) => r.data),

    // Phase 3
    reviewProtocol: (id) =>
        client.get(`/businesses/${id}/review-protocol`).then((r) => r.data),
    updateReviewProtocol: (id, patch) =>
        client.put(`/businesses/${id}/review-protocol`, patch).then((r) => r.data),
    regenReviewProtocol: (id) =>
        client.post(`/businesses/${id}/review-protocol/regenerate`).then((r) => r.data),

    igComments: (id, params = {}) =>
        client.get(`/businesses/${id}/ig-comments`, { params }).then((r) => r.data),
    commentAction: (cid, action) =>
        client.post(`/ig-comments/${cid}/action`, { action }).then((r) => r.data),
    commentGenReply: (cid) =>
        client.post(`/ig-comments/${cid}/generate_reply`).then((r) => r.data),
    commentModeration: (id) =>
        client.get(`/businesses/${id}/comment-moderation`).then((r) => r.data),
    updateCommentModeration: (id, rules) =>
        client.put(`/businesses/${id}/comment-moderation`, { rules }).then((r) => r.data),
    setLiveAutoModeration: (id, live_auto) =>
        client.put(`/businesses/${id}/comment-moderation`, { live_auto }).then((r) => r.data),

    integrations: (id) =>
        client.get(`/businesses/${id}/integrations`).then((r) => r.data),

    gbpLocations: (id) =>
        client.get(`/businesses/${id}/gbp/locations`).then((r) => r.data),

    gbpLocationBinding: (id) =>
        client.get(`/businesses/${id}/gbp/location-binding`).then((r) => r.data),

    gbpSyncStatus: (id) =>
        client.get(`/businesses/${id}/gbp/sync-status`).then((r) => r.data),

    gbpSaveLocation: (id, location_name) =>
        client.post(`/businesses/${id}/gbp/location-binding`, {
            location_name,
        }).then((r) => r.data),

    connectIntegration: (id, provider) =>
        client.post(`/businesses/${id}/integrations/${provider}/connect`).then((r) => r.data),
    reconnectIntegration: (id, provider) =>
        client.post(`/businesses/${id}/integrations/${provider}/reconnect`).then((r) => r.data),
    disconnectIntegration: (id, provider) =>
        client.post(`/businesses/${id}/integrations/${provider}/disconnect`).then((r) => r.data),
    syncIntegration: (id, provider) =>
        client.post(`/businesses/${id}/integrations/${provider}/sync`).then((r) => r.data),

    // Phase 6 — WhatsApp
    waConnection: (id) =>
        client.get(`/businesses/${id}/whatsapp/connection`).then((r) => r.data),
    waSaveConnection: (id, payload) =>
        client.put(`/businesses/${id}/whatsapp/connection`, payload).then((r) => r.data),
    waVerify: (id) =>
        client.post(`/businesses/${id}/whatsapp/verify`).then((r) => r.data),
    waDisconnect: (id) =>
        client.post(`/businesses/${id}/whatsapp/disconnect`).then((r) => r.data),
    waThreads: (id, status) =>
        client.get(`/businesses/${id}/whatsapp/threads`, {
            params: status ? { status } : {},
        }).then((r) => r.data),
    waThread: (id, tid) =>
        client.get(`/businesses/${id}/whatsapp/threads/${tid}`).then((r) => r.data),
    waSimulateIncoming: (id, payload) =>
        client.post(`/businesses/${id}/whatsapp/simulate-incoming`, payload).then((r) => r.data),
    waStaffReply: (tid, text) =>
        client.post(`/whatsapp/threads/${tid}/reply`, { text }).then((r) => r.data),
    waReturnToAi: (tid) =>
        client.post(`/whatsapp/threads/${tid}/return-to-ai`).then((r) => r.data),
    waResolveThread: (tid) =>
        client.post(`/whatsapp/threads/${tid}/resolve`).then((r) => r.data),
    waGetAutonomy: (id) =>
        client.get(`/businesses/${id}/whatsapp/autonomy`).then((r) => r.data),
    waUpdateAutonomy: (id, rules) =>
        client.put(`/businesses/${id}/whatsapp/autonomy`, rules).then((r) => r.data),
    waListRules: (id) =>
        client.get(`/businesses/${id}/whatsapp/rules`).then((r) => r.data),
    waParseRule: (id, natural_language) =>
        client.post(`/businesses/${id}/whatsapp/rules/parse`, { natural_language }).then((r) => r.data),
    waSaveRule: (id, parsed, enabled = true) =>
        client.post(`/businesses/${id}/whatsapp/rules`, { parsed, enabled }).then((r) => r.data),
    waToggleRule: (rid, enabled) =>
        client.patch(`/whatsapp/rules/${rid}`, { enabled }).then((r) => r.data),
    waDeleteRule: (rid) =>
        client.delete(`/whatsapp/rules/${rid}`).then((r) => r.data),
    waReport: (id, days = 30) =>
        client.get(`/businesses/${id}/whatsapp/report`, { params: { days } }).then((r) => r.data),

    // Phase 7 — SEO Engine
    seoStartScan: (id, payload) =>
        client.post(`/businesses/${id}/seo/scan`, payload).then((r) => r.data),
    seoGetScan: (id, sid, include_pages = true) =>
        client.get(`/businesses/${id}/seo/scans/${sid}`, {
            params: { include_pages },
        }).then((r) => r.data),
    seoLatest: (id) =>
        client.get(`/businesses/${id}/seo/latest`).then((r) => r.data),
    seoScans: (id) =>
        client.get(`/businesses/${id}/seo/scans`).then((r) => r.data),
    seoFixWithAI: (id, payload) =>
        client.post(`/businesses/${id}/seo/fix-with-ai`, payload).then((r) => r.data),
    googleStatus: (id) =>
        client.get(`/businesses/${id}/google/status`).then((r) => r.data),
    googleOauthUrl: (id, provider, redirect_uri) =>
        client.get(`/businesses/${id}/google/oauth-url`, {
            params: { provider, redirect_uri },
        }).then((r) => r.data),
    googleExchangeCode: (id, payload) =>
        client.post(`/businesses/${id}/google/exchange-code`, payload).then((r) => r.data),
    googleVerify: (id, provider) =>
        client.post(`/businesses/${id}/google/${provider}/verify`).then((r) => r.data),
    googleDisconnect: (id, provider) =>
        client.post(`/businesses/${id}/google/${provider}/disconnect`).then((r) => r.data),
    dataDeletionStatus: (code) =>
        client.get("/instagram/data-deletion/status", { params: { code } }).then((r) => r.data),
    googleInsights: (id) =>
        client.get(`/businesses/${id}/google/insights`).then((r) => r.data),
    googleSelect: (id, provider, value) =>
        client.put(`/businesses/${id}/google/${provider}/select`, { value }).then((r) => r.data),

    // Phase 8 — AEO / GEO / AI Visibility / Competitors
    competitorsList: (id) =>
        client.get(`/businesses/${id}/competitors`).then((r) => r.data),
    competitorsAdd: (id, payload) =>
        client.post(`/businesses/${id}/competitors`, payload).then((r) => r.data),
    competitorsDelete: (id, cid) =>
        client.delete(`/businesses/${id}/competitors/${cid}`).then((r) => r.data),
    competitorsSuggest: (id) =>
        client.post(`/businesses/${id}/competitors/suggest`).then((r) => r.data),
    competitorsAnalyse: (id) =>
        client.post(`/businesses/${id}/competitors/analyse`).then((r) => r.data),
    visibilityTest: (id, payload) =>
        client.post(`/businesses/${id}/visibility/test`, payload).then((r) => r.data),
    visibilityHistory: (id) =>
        client.get(`/businesses/${id}/visibility/history`).then((r) => r.data),
    aeoGenerateAnswer: (id, payload) =>
        client.post(`/businesses/${id}/aeo/generate-answer`, payload).then((r) => r.data),
};

export default api;
